import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useFamily,
  useCreateFamily,
  useUpdateFamily,
  useAddAttributeGroupToFamily,
  useRemoveAttributeGroupFromFamily,
} from '@/api/familyApi';
import { useAttributeGroupsWithUsage } from '@/api/attributeGroupApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  PlusIcon,
  Trash2Icon,
  GripVertical,
  SearchIcon,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { toast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { logDebug } from '@/utils/errorHandler';

// Form validation schema
const FamilySchema = z.object({
  code: z.string().min(1, { message: 'families.validation.codeRequired' }),
  label: z.string().min(1, { message: 'families.validation.labelRequired' }),
  description: z.string().optional(),
  attributeGroups: z
    .array(
      z.object({
        id: z.number().optional(),
        attribute_group: z.number(),
        attribute_group_name: z.string().optional(),
        order: z.number().int().nonnegative(),
      })
    )
    .min(1, { message: 'families.validation.attributeGroupsRequired' }),
});

type FamilyFormValues = z.infer<typeof FamilySchema>;

interface FamilyFormPageProps {
  mode: 'create' | 'edit';
}

// Sortable item component for attribute groups
function SortableGroupItem({
  id,
  index,
  item,
  availableGroups,
  onRemove,
}: {
  id: string;
  index: number;
  item: any;
  availableGroups: any[];
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  // Apply transform styles from DnD Kit
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Find the group to get usage information
  const groupInfo = availableGroups.find(g => g.id === item.attribute_group);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-3 rounded-md border bg-card p-3"
    >
      <div
        className="cursor-grab text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-5" />
      </div>

      <div className="flex flex-1 items-center space-x-4">
        <div className="w-full sm:w-64">
          <Label htmlFor={`attributeGroups.${index}.attribute_group`}>
            {t('families.form.group')}
          </Label>
          <div className="mt-1">
            <div className="block rounded-md border bg-muted/50 p-2">
              <div className="text-sm font-medium">
                {item.attribute_group_name || groupInfo?.name || `Group #${item.attribute_group}`}
              </div>
              {groupInfo?.usage && (
                <div className="mt-1 flex flex-wrap gap-1">
                  <span className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
                    {groupInfo.usage.productCount} {t('families.usage.products')}
                  </span>
                  <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {groupInfo.usage.attributeCount} {t('families.usage.attrs')}
                  </span>
                  {groupInfo.usage.hasValues && (
                    <span className="inline-flex items-center rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-600 dark:bg-green-950/50 dark:text-green-300">
                      {t('families.usage.hasValues')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(index)}
        className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50"
      >
        <Trash2Icon className="size-4" />
        <span className="sr-only">{t('common.actions.remove')}</span>
      </Button>
    </div>
  );
}

export function FamilyFormPage({ mode }: FamilyFormPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : 0;
  const [searchTerm, setSearchTerm] = useState('');
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Get data from API
  const { data: family, isLoading: isFamilyLoading } = useFamily(numericId);
  const {
    data: groups,
    isLoading: isGroupsLoading,
    error: groupsError,
  } = useAttributeGroupsWithUsage();
  const createFamily = useCreateFamily();
  const updateFamily = useUpdateFamily(numericId);

  // Query client for manual cache management
  const queryClient = useQueryClient();

  // Manual refresh function
  const refreshGroups = () => {
    queryClient.invalidateQueries({ queryKey: ['attributeGroups'] });
    toast({
      title: 'Refreshing attribute groups...',
      description: 'Loading latest data from server.',
    });
  };

  // Form setup
  const form = useForm<FamilyFormValues>({
    resolver: zodResolver(FamilySchema),
    defaultValues: {
      code: '',
      label: '',
      description: '',
      attributeGroups: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset,
    watch,
    getValues,
  } = form;

  // Setup attribute groups field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'attributeGroups',
  });

  // Load form data when editing
  useEffect(() => {
    if (mode === 'edit' && family) {
      reset({
        code: family.code,
        label: family.label,
        description: family.description || '',
        attributeGroups:
          family.attribute_groups?.map(group => ({
            id: group.id,
            attribute_group: group.attribute_group,
            attribute_group_name: group.attribute_group_object?.name,
            order: group.order,
          })) || [],
      });
    }
  }, [mode, family, reset]);

  // DnD setup for sortable groups
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track current location
  const location = useLocation();
  const prevLocationRef = useRef(location);

  // Handle browser navigation (refresh, close, etc)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Handle in-app navigation
  useEffect(() => {
    if (prevLocationRef.current !== location && isDirty) {
      // If location changed and there are unsaved changes
      setShowUnsavedDialog(true);
    }
    prevLocationRef.current = location;
  }, [location, isDirty]);

  // Handle form submission
  const onSubmit = async (data: FamilyFormValues) => {
    try {
      // build payload matching Django serializer
      const payload = {
        code: data.code,
        label: data.label,
        description: data.description,
        attribute_groups: data.attributeGroups.map(g => ({
          attribute_group: g.attribute_group,
          order: g.order,
        })),
      };

      if (mode === 'create') {
        await createFamily.mutateAsync(payload);
        toast({
          title: t('families.messages.saveSuccess'),
          variant: 'default',
        });
      } else {
        await updateFamily.mutateAsync(payload);
        toast({
          title: t('families.messages.saveSuccess'),
          variant: 'default',
        });
      }
      navigate(settingsFamiliesUrl);
    } catch (err: any) {
      if (err?.message?.includes('attribute group')) {
        toast({
          title: t('families.messages.attributeGroupRace'),
          description: t('families.messages.attributeGroupRaceDesc'),
          variant: 'destructive',
        });
        // Optionally reload attribute groups
        // refetch attribute groups here if needed
      } else {
        toast({
          title: t('common.messages.error'),
          description: err?.message || t('common.messages.unknownError'),
          variant: 'destructive',
        });
      }
    }
  };

  // Filter groups based on search term
  const filteredGroups =
    groups?.filter(
      group => group?.name && group.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Get already selected group IDs
  const selectedGroupIds = watch('attributeGroups').map(g => g.attribute_group);

  // Get available groups (groups not yet selected)
  const availableGroups = filteredGroups.filter(group => !selectedGroupIds.includes(group.id));

  // Check if form is loading
  const isLoading = (mode === 'edit' && isFamilyLoading) || isGroupsLoading;

  // Add a new attribute group
  const addAttributeGroup = (groupId: number, groupName: string) => {
    append({
      attribute_group: groupId,
      attribute_group_name: groupName,
      order: fields.length,
    });
  };

  // Settings page URL with families tab
  const settingsFamiliesUrl = '/app/settings?tab=families';

  // Update the function names to be clearer
  const handleStayOnPage = () => {
    setShowUnsavedDialog(false);
    // Just close the dialog, stay on current page
  };

  const handleDiscardAndNavigate = () => {
    setShowUnsavedDialog(false);
    // Reset the form and allow navigation
    reset();
    // Navigate to the families tab in settings
    navigate(settingsFamiliesUrl);
  };

  // Handle DnD end event
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(
        field => `group-${field.id || field.attribute_group}` === active.id
      );
      const newIndex = fields.findIndex(
        field => `group-${field.id || field.attribute_group}` === over.id
      );

      move(oldIndex, newIndex);
    }
  };

  // Debug logging
  useEffect(() => {
    logDebug('FamilyFormPage Debug', {
      groups,
      isGroupsLoading,
      groupsError,
      groupsLength: groups?.length,
      filteredGroupsLength: filteredGroups.length,
      availableGroupsLength: availableGroups.length,
      selectedGroupIds,
      searchTerm,
      // Add detailed group inspection
      sampleGroup: groups?.[0],
      groupNames: groups?.map(g => ({
        id: g.id,
        name: g.name,
        hasName: !!g?.name,
      })),
    });
  }, [
    groups,
    isGroupsLoading,
    groupsError,
    filteredGroups.length,
    availableGroups.length,
    selectedGroupIds,
    searchTerm,
  ]);

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (isDirty) {
                setShowUnsavedDialog(true);
              } else {
                navigate(settingsFamiliesUrl);
              }
            }}
          >
            <ArrowLeft className="mr-2 size-4" />
            {t('families.form.back')}
          </Button>
          <h1 className="text-2xl font-bold">
            {mode === 'create'
              ? t('families.createNew')
              : t('families.edit', { code: family?.code || '' })}
          </h1>
        </div>

        {isLoading ? (
          <div className="animate-pulse space-y-6">
            <div className="space-y-3">
              <div className="h-5 w-32 rounded bg-enterprise-200" />
              <div className="h-10 w-full rounded bg-enterprise-200" />
            </div>
            <div className="space-y-3">
              <div className="h-5 w-32 rounded bg-enterprise-200" />
              <div className="h-10 w-full rounded bg-enterprise-200" />
            </div>
            <div className="space-y-3">
              <div className="h-5 w-32 rounded bg-enterprise-200" />
              <div className="h-24 w-full rounded bg-enterprise-200" />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">
                    {t('families.form.code')}
                    <span className="ml-1 text-red-500">*</span>
                  </Label>
                  <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                      <Input id="code" {...field} className={errors.code ? 'border-red-300' : ''} />
                    )}
                  />
                  {errors.code && (
                    <p className="text-sm text-red-500">{t(errors.code.message as string)}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="label">
                    {t('families.form.label')}
                    <span className="ml-1 text-red-500">*</span>
                  </Label>
                  <Controller
                    name="label"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="label"
                        {...field}
                        className={errors.label ? 'border-red-300' : ''}
                      />
                    )}
                  />
                  {errors.label && (
                    <p className="text-sm text-red-500">{t(errors.label.message as string)}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t('families.form.description')}</Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => <Textarea id="description" {...field} rows={4} />}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">
                    {t('families.form.attributeGroups')}
                    <span className="ml-1 text-red-500">*</span>
                  </Label>

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={refreshGroups}>
                      <RefreshCw className="mr-2 size-4" />
                      {t('common.refresh', 'Refresh')}
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="sm">
                          <PlusIcon className="mr-2 size-4" />
                          {t('families.form.addGroup')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-4">
                        <div className="space-y-4">
                          <h4 className="font-medium">{t('families.form.addGroup')}</h4>

                          <div className="relative">
                            <SearchIcon className="absolute left-3 top-3 size-4 text-muted-foreground" />
                            <Input
                              placeholder={t('families.form.searchPlaceholder')}
                              value={searchTerm}
                              onChange={e => setSearchTerm(e.target.value)}
                              className="pl-9"
                            />
                          </div>

                          <div className="max-h-60 space-y-2 overflow-y-auto">
                            {isGroupsLoading ? (
                              <div className="p-2 text-sm text-muted-foreground">
                                {t('common.loading', 'Loading attribute groups...')}
                              </div>
                            ) : groupsError ? (
                              <div className="p-2 text-sm text-red-500">
                                {t('common.error', 'Error loading groups:')}{' '}
                                {groupsError instanceof Error
                                  ? groupsError.message
                                  : t('common.unknownError', 'Unknown error')}
                              </div>
                            ) : availableGroups.length > 0 ? (
                              availableGroups.map(group => (
                                <div
                                  key={group.id}
                                  className="cursor-pointer rounded-md border p-3 hover:bg-muted/50"
                                  onClick={() => addAttributeGroup(group.id, group.name)}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h5 className="text-sm font-medium">{group.name}</h5>
                                      {group.usage && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                                            {group.usage.productCount}{' '}
                                            {t('families.usage.products')}
                                          </span>
                                          <span className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">
                                            {group.usage.attributeCount}{' '}
                                            {t('families.usage.attributes')}
                                          </span>
                                          {group.usage.hasValues && (
                                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs text-green-700 dark:bg-green-950/50 dark:text-green-300">
                                              {t('families.usage.hasValues')}
                                            </span>
                                          )}
                                          {group.usage.familyCount > 0 && (
                                            <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-1 text-xs text-purple-700 dark:bg-purple-950/50 dark:text-purple-300">
                                              {group.usage.familyCount}{' '}
                                              {t('families.usage.families')}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="ml-2 size-6 p-0"
                                    >
                                      <PlusIcon className="size-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="p-2 text-sm text-muted-foreground">
                                {searchTerm
                                  ? t('families.form.noGroupsFound', 'No attribute groups found')
                                  : t(
                                      'families.form.noGroupsAvailable',
                                      'No attribute groups available'
                                    )}
                              </p>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {errors.attributeGroups && (
                  <div className="flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-700">
                    <AlertTriangle className="size-4" />
                    <p className="text-sm">{t(errors.attributeGroups.message as string)}</p>
                  </div>
                )}

                <div className="space-y-3">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.map(field => `group-${field.id || field.attribute_group}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      {fields.map((field, index) => (
                        <SortableGroupItem
                          key={`group-${field.id || field.attribute_group}`}
                          id={`group-${field.id || field.attribute_group}`}
                          index={index}
                          item={field}
                          availableGroups={groups || []}
                          onRemove={remove}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {fields.length === 0 && (
                    <div className="rounded-md border border-dashed p-6 text-center">
                      <p className="text-muted-foreground">{t('families.form.noGroups')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isDirty) {
                    setShowUnsavedDialog(true);
                  } else {
                    navigate(settingsFamiliesUrl);
                  }
                }}
              >
                {t('families.form.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isValid || form.formState.isSubmitting}
              >
                {t('families.form.save')}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Unsaved changes dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.dialogs.unsavedChanges')}</DialogTitle>
            <DialogDescription>{t('common.messages.unsavedChangesDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleStayOnPage}>
              {t('families.form.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDiscardAndNavigate}>
              {t('common.actions.discard')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}
